/**
 * Canvas UI 2.0 — curated Refine prompts, per designer node type.
 *
 * Ten entries each for Character (expression / outfit / skin / age), Object
 * (material & finish) and Location (lighting & mood), transcribed verbatim from
 * the Figma frames. Picking one drops its text into the Refine feedback box,
 * where the user can edit it before generating — it is a starting point, not a
 * committed action.
 *
 * ## Why the prompt bodies are English and not translated
 *
 * These are MODEL DIRECTION, not prose anyone reads or hears: they describe
 * lighting, materials and facial structure to an image model. CLAUDE.md's
 * content-language rules scope translation to "spoken/read prose" and
 * explicitly keep shot/layout codes and fixed taxonomies English for exactly
 * this reason — localizing them degrades generation. The user-visible TITLES do
 * localize, via `promptLibrary.<id>` in the canvas namespace.
 *
 * That also keeps the i18n surface honest: 30 short titles across four locales
 * is reviewable, 30 paragraphs across four locales is not.
 *
 * ## Why this is a constant and not a DB table
 *
 * The frames specify a fixed, curated set. Nothing in them implies per-user or
 * per-project editing, and a table would pull in the rules-store machinery for
 * data that ships with the app. If editable libraries are wanted later this
 * becomes the seed, not an obstacle.
 */

/** One curated prompt. `id` keys both the i18n title and the test fixtures. */
export interface PromptLibraryEntry {
  /** Stable slug — also the i18n key suffix (`promptLibrary.<id>`). */
  id: string
  /** The prompt text inserted into the Refine feedback box. English by design. */
  prompt: string
}

const CHARACTER_PROMPTS: readonly PromptLibraryEntry[] = [
  {
    id: 'expressionWarmSmile',
    prompt:
      'Modify facial expression to a warm genuine smile, relaxed soft gaze, gentle crinkling around the eyes, natural facial muscle shift, keeping character design and base art style identical.',
  },
  {
    id: 'expressionIntenseAnger',
    prompt:
      'Modify facial expression to intense anger: furrowed brow, narrowed sharp gaze, clenched jaw, subtle tension lines across the face, keeping character design and base art style identical.',
  },
  {
    id: 'expressionSkepticalSmirk',
    prompt:
      'Modify facial expression to a skeptical smirk: one raised eyebrow, subtle lopsided smile, searching gaze, keeping character design and base art style identical.',
  },
  {
    id: 'outfitHeavyTacticalArmor',
    prompt:
      'Change character outfit to heavy tactical armor: layered metal plates, reinforced leather straps, utility pouches, high collar, keeping facial features, hair, and base art style identical.',
  },
  {
    id: 'outfitElegantFormalWear',
    prompt:
      'Change character outfit to elegant formal attire: tailored fitted suit jacket, crisp high-collared shirt, fine fabric texture, keeping facial features, hair, and base art style identical.',
  },
  {
    id: 'outfitLayeredTravelingRobes',
    prompt:
      'Change character outfit to weathered traveling robes: heavy draped fabric, high hood rested on shoulders, cloth wraps, leather belt with pouches, keeping facial features, hair, and base art style identical.',
  },
  {
    id: 'skinScarsBattleDamage',
    prompt:
      'Add facial skin details: subtle healed scar across cheekbone, light surface scratches, faint smudge of soot and dust across skin, keeping character features, outfit, and base art style identical.',
  },
  {
    id: 'skinGlowingMarkings',
    prompt:
      'Add facial skin details: subtle glowing geometric rune markings tracing the cheeks and jawline, faint soft luminescence on skin, keeping base character features and art style identical.',
  },
  {
    id: 'ageElderVersion',
    prompt:
      'Modify age of character to elder: add fine wrinkles around eyes and mouth, silver graying streaks to hair, slightly weathered skin texture, keeping character identity, outfit, and base art style identical.',
  },
  {
    id: 'ageYoungerVersion',
    prompt:
      'Modify age of character to younger: smooth unblemished skin, softer rounded facial contours, slightly larger bright eyes, keeping character identity, hair style, and base art style identical.',
  },
]

const OBJECT_PROMPTS: readonly PromptLibraryEntry[] = [
  {
    id: 'materialMatteIndustrial',
    prompt:
      'Change object material to raw industrial matte: brushed gunmetal accents, micro-textured poured concrete, subtle seam lines, soft studio ambient lighting, slate gray and charcoal tones, tactile, utilitarian aesthetic.',
  },
  {
    id: 'materialWarmOrganic',
    prompt:
      'Change object material to handcrafted natural oak: rich visible wood grain, smooth satin finish, soft rounded organic edges, warm directional spotlighting, cozy tactile warmth, high-end Scandinavian craft aesthetic.',
  },
  {
    id: 'materialSleekLuxury',
    prompt:
      'Change object material to mirror-polished liquid chrome: razor-sharp rim lighting, high-contrast dark room reflections, flawless high-gloss finish, metallic sheen, premium luxury aesthetic.',
  },
  {
    id: 'materialTranslucentMatte',
    prompt:
      'Change object material to semi-translucent frosted glass: soft internal light diffusion, subtle refraction, matte satin texture, clean pastel tint, minimalist modern product design.',
  },
  {
    id: 'materialHighTechPerformance',
    prompt:
      'Change object material to high-performance carbon fiber weave: matte black anodized aluminum trim, crisp geometric edge highlights, durable motorsport finish, sleek tactical tech aesthetic.',
  },
  {
    id: 'materialSoftTactile',
    prompt:
      'Change object material to fine-grain pebbled leather and heavy woven linen fabric: visible stitching details, soft matte surface, gentle highlight falloff, warm tactile luxury aesthetic.',
  },
  {
    id: 'materialArtisanalCeramic',
    prompt:
      'Change object material to handcrafted matte ceramic: subtle hand-molded surface irregularities, raw clay texture base, soft satin glaze finish, warm earthy terracotta tones, organic studio aesthetic.',
  },
  {
    id: 'materialFuturisticIridescent',
    prompt:
      'Change object material to iridescent pearlescent finish: color-shifting sheen ranging from cyan to magenta, glossy reflective coating, dynamic specular highlights, futuristic product design aesthetic.',
  },
  {
    id: 'materialAgedVintage',
    prompt:
      'Change object material to antiqued brushed brass: subtle verdigris patina in crevices, worn soft-burnished edges, warm golden undertones, rich historical tactile character, vintage luxury.',
  },
  {
    id: 'materialMinimalistMonolithic',
    prompt:
      'Change object material to seamless soft-touch matte silicone: velvety non-reflective finish, ultra-smooth continuous surfaces, subtle rounded fillets, uniform muted color palette, modern tech aesthetic.',
  },
]

const LOCATION_PROMPTS: readonly PromptLibraryEntry[] = [
  {
    id: 'lightingNightCozyAmbient',
    prompt:
      'Change mood to nighttime: deep shadow contrast, warm glow from ambient light sources, rich dark moody palette, soft reflections, cozy sophisticated night atmosphere.',
  },
  {
    id: 'lightingGoldenHour',
    prompt:
      'Change lighting to warm golden hour: low-angle directional sunlight, long soft shadows, warm amber and bronze tones, atmospheric light haze, peaceful inviting mood.',
  },
  {
    id: 'lightingMoodyStorm',
    prompt:
      'Change atmosphere to moody rainy day: cool slate-blue and gray color palette, high moisture sheen on reflective surfaces, soft diffused gloomy light, storm-charged mood.',
  },
  {
    id: 'lightingColdCrispWinter',
    prompt:
      'Change tone to cold winter mood: pale icy blue ambient lighting, cool color temperature, crisp contrast, clean chilly atmosphere.',
  },
  {
    id: 'lightingOvercastDiffused',
    prompt:
      'Change lighting to soft overcast daylight: smooth shadowless illumination, muted desaturated neutral tones, calm serene aesthetic, soft light diffusion.',
  },
  {
    id: 'lightingBlueHourDusk',
    prompt:
      'Change mood to twilight blue hour: deep blue sky tones, warm contrasting light sources, low-contrast shadows, sleek high-end evening mood.',
  },
  {
    id: 'lightingCyberpunkNeon',
    prompt:
      'Change lighting to dramatic cyberpunk night: dark moody background with high-contrast neon accents, vivid magenta and cyan ambient glow, glossy reflective surfaces, cinematic low-key lighting.',
  },
  {
    id: 'lightingEtherealDawnFog',
    prompt:
      'Change atmosphere to early morning dawn: soft pastel pink and pale gold sunrise light, gentle low-lying mist and atmospheric haze, soft god rays, dreamlike calm ethereal mood.',
  },
  {
    id: 'lightingHighSunSummer',
    prompt:
      'Change lighting to intense summer midday: bright direct sunlight, crisp geometric high-contrast shadows, warm beige and saturated vibrant tones, clear dry high-energy mood.',
  },
  {
    id: 'lightingDesaturatedCinematic',
    prompt:
      'Change color grading to moody filmic style: deeply desaturated color palette, lifted shadow tones, matte finish, cool slate and earthy contrast, subtle film grain, dramatic grounded tone.',
  },
]

/**
 * Curated prompts by frontend node type.
 *
 * Only the three designers the Canvas UI 2.0 frames cover have libraries.
 * `imageGenerator` and `storyboardDesigner` are deliberately absent rather than
 * given a borrowed set: a character-expression prompt is meaningless on a
 * storyboard panel, and `promptLibraryFor` returning an empty list is what
 * hides the control on those nodes.
 */
export const PROMPT_LIBRARY: Readonly<Record<string, readonly PromptLibraryEntry[]>> = {
  characterDesigner: CHARACTER_PROMPTS,
  objectDesigner: OBJECT_PROMPTS,
  locationDesigner: LOCATION_PROMPTS,
}

/**
 * Curated prompts for a node type, or an empty list when it has none.
 *
 * @param nodeType - Frontend node type (e.g. `characterDesigner`).
 */
export function promptLibraryFor(
  nodeType: string | undefined,
): readonly PromptLibraryEntry[] {
  if (!nodeType) return []
  return PROMPT_LIBRARY[nodeType] ?? []
}
